import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import store from '@/store/index';
import { TokenService } from '@/utils/token.service';
import { webAuth } from '@/utils/auth';

const validateStatus = status => {
  return status >= 200 && status < 300;
};

const refreshAuthLogic = failedRequest =>
  webAuth.checkSession({}, async (err, authResult) => {
    if (authResult && authResult.accessToken && authResult.idToken) {
      const expiresAt = JSON.stringify(
        authResult.expiresIn * 1000 + new Date().getTime()
      );
      localStorage.setItem('expires_at', expiresAt);
      localStorage.setItem('access_token', authResult.accessToken);
      localStorage.setItem('id_token', authResult.idToken);
      localStorage.setItem('user', JSON.stringify(authResult.idTokenPayload));

      axios.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${authResult.accessToken}`;

      return axios(failedRequest);
    } else if (err) {
      store.dispatch('showUserMessage', {
        success: false,
        persist: true,
        title: 'Something went wrong!',
        message: 'Your session has time out. Please login again.',
      });
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('expires_at');
      localStorage.removeItem('user');
      webAuth.logout({
        returnTo: process.env.VUE_APP_AUTH0_REDIRECT, // Allowed logout URL listed in dashboard
        clientID: process.env.VUE_APP_AUTH0_CLIENT_ID, // Your client ID
      });
      return err;
    }
  });

const checkError = error => {
  return error.response || error.request || error.message;
};

export const apiService = {
  init: baseURL => {
    axios.defaults.baseURL = baseURL;
    axios.defaults.validateStatus = validateStatus;
    createAuthRefreshInterceptor(axios, refreshAuthLogic);
  },

  setHeader: () => {
    axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${TokenService.getToken()}`;
  },

  get: async resource => {
    store.dispatch('setLoading', true);
    try {
      const { data } = await axios.get(resource);
      setTimeout(async () => {
        store.dispatch('setLoading', false);
      }, 5);
      return data;
    } catch (e) {
      return checkError(e);
    }
  },

  tabGet: async resource => {
    store.dispatch('setTabLoading', true);
    try {
      const { data } = await axios.get(resource);
      store.dispatch('setTabLoading', false);
      return data;
    } catch (e) {
      return checkError(e);
    }
  },

  post: async (resource, data) => {
    try {
      return await axios.post(resource, data);
    } catch (e) {
      return checkError(e);
    }
  },

  put: async (resource, data) => {
    try {
      return await axios.put(resource, data);
    } catch (e) {
      return checkError(e);
    }
  },

  patch: async (resource, data) => {
    try {
      return await axios.patch(resource, data);
    } catch (e) {
      return checkError(e);
    }
  },

  delete: async resource => {
    try {
      await axios.delete(resource);
      return true;
    } catch (e) {
      return checkError(e);
    }
  },
};
